import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { context } from '@adornis/baseql/server/context.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import type { UserPermission } from '@adornis/users/db/types.js';
import { validate } from '@adornis/validation/decorators.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { type Permission, Role } from '../enums/roles.js';
import { mongo } from '../server/mongo.js';
import { notifyUserPermissionChange } from '../server/user-change-notifier.js';
import { Participation } from './Participation.js';
import { UserStammdaten } from './stammdaten.js';
import { Suspension } from './suspension.js';

// create indices
export async function ensureUserIndexes(): Promise<void> {
  try {
    const userCollection = mongo().collection(AdornisUser._collectionName);
    await userCollection.createIndex({ 'suspentions.blockedTill': 1 });
  } catch (err) {
    logger.warn(
      { err },
      'Could not create index on suspentions.blockedTill (may already exist with different options)',
    );
  }
  try {
    const userCollection = mongo().collection(AdornisUser._collectionName);
    await userCollection.createIndex({ 'roles.name': 1, 'roles.contexts': 1 });
  } catch (err) {
    logger.warn({ err }, 'Could not create index on roles.name and roles.contexts, continuing');
  }
}

@Entity()
export class User extends AdornisUser<Permission | UserPermission> {
  static override _class = 'User';

  constructor(doc: PartialEntityData<User>) {
    super(doc);
  }

  @Field(type => String)
  samlID?: string;

  @Field(type => String)
  samlDepartmentCode?: string;

  @Field(type => String)
  samlMatrikelnummer?: string;

  @Field(type => String)
  samlGender?: string;

  @Field(type => String)
  title?: string;

  @validate(nonOptional())
  @Field(type => String)
  firstName!: string;

  @validate(nonOptional())
  @Field(type => String)
  lastName!: string;

  @DataLoadedField(type => [Participation], ({ _id }) => ({ userID: _id }))
  participations!: Participation[];

  //! this is necessary to show stammdaten in the user-export-view
  @Field(type => UserStammdaten, {
    resolve: function (this: User) {
      return async gqlFields => {
        if (this.stammdaten) return this.stammdaten; // do not refetch
        const institutionID = (context as any).institutionID;
        if (!institutionID) {
          // TODO: remove <Entity>.allFields from codebase, so that this works with the error is place
          return null; // throwing an error breaks to much stuff right now
          throw new Error('this fiel should only be requsted when the context includes an institutionID');
        }
        const { getExistingStammdaten } = await import('../api/stammdaten.js');
        const stammdaten = await getExistingStammdaten(this._id, institutionID, null)(gqlFields);
        return stammdaten as UserStammdaten;
      };
    },
  })
  stammdaten: Maybe<UserStammdaten>;

  //! this is necessary to show suspended status in the admin-institution-particpants view
  @Field(type => Boolean, {
    resolve: function (this: User) {
      return async gqlFields => {
        const institutionID = (context as any).institutionID;
        if (!institutionID) return null;
        return !!this.suspentions?.find(s => s.institutionID === institutionID);
      };
    },
  })
  suspended: Maybe<boolean>;

  @Field(type => String)
  stripeCustomerID?: string;

  @validate(nonOptional())
  @Field(type => [Suspension], { default: v => v ?? [] })
  suspentions!: Suspension[];

  @Field(type => Boolean, { default: v => v ?? false })
  whatsappNotification!: boolean;

  @Field(type => String)
  whatsappPhone: Maybe<string>;

  @Field(type => Boolean, { default: v => v ?? false })
  receivesNewsletter!: boolean;

  @Field(type => String)
  zoomAccessTokenID: Maybe<string>;

  // optional registration fields
  @Field(() => Date)
  lastActiveAt: Maybe<Date>;

  @Field(() => Date)
  dateOfBirth: Maybe<Date>;

  // sd fields
  @Field(() => Int)
  sd_customerNr: Maybe<number>;

  get isSuperAdmin() {
    return !!this.roles.find(role => role.name === Role.SUPER_ADMIN);
  }

  get isAdmin() {
    return !!this.roles.find(role => role.name === Role.ADMIN || role.name === Role.SUPER_ADMIN);
  }

  get adminRole() {
    return this.roles.find(role => role.name === Role.ADMIN);
  }

  get isBerater() {
    return !!this.roles.find(role => role.name === Role.BERATER);
  }

  get isParticipant() {
    return !!this.roles.find(role => role.name === Role.PARTICIPANT);
  }

  get isLecturer() {
    return !!this.roles.find(role => role.name === Role.LECTURER);
  }

  get lecturerRole() {
    return this.roles.find(role => role.name === Role.LECTURER);
  }

  /** all institutions the user is a lecturer in; `null` means a global lecturer (= all institutions) */
  get lecturerContexts(): string[] | null {
    return lecturerContextsOf(this.roles);
  }

  isLecturerIn(institutionID: string) {
    return isLecturerInInstitution(this.roles, institutionID);
  }

  static override async afterUpdate(oldEntity: User, update: User): Promise<void> {
    // @ts-expect-error this is only not optional so that the DX doesn't get confused
    delete oldEntity.password;

    const userID = (update as User)?._id ?? oldEntity?._id;
    if (userID) {
      if (rolesDiffer(oldEntity?.roles, (update as User)?.roles)) {
        notifyUserPermissionChange({ userID, kind: 'roles' });
      } else if (suspensionsDiffer(oldEntity?.suspentions, (update as User)?.suspentions)) {
        notifyUserPermissionChange({ userID, kind: 'suspensions' });
      }
    }
  }

  static override async transformBeforeUpdate(oldEntity: User, update: User): Promise<void> {
    await super.transformBeforeUpdate(oldEntity, update);

    let isSuper = false;
    let isAdminOther = false;
    let isSelf = false;
    if (!context.serverContext) {
      const { getCurrentUserServer } = await import('@adornis/bh-util/util.js');
      const caller = await getCurrentUserServer();
      const callerRoles = caller?.roles ?? [];
      isSuper =
        caller?.isSuperAdmin === true ||
        callerRoles.some((role: { name: string }) => role.name === Role.SUPER_ADMIN);
      const isAdmin =
        caller?.isAdmin === true || callerRoles.some((role: { name: string }) => role.name === Role.ADMIN);
      isSelf = caller?._id === oldEntity._id;
      const targetIsAdmin = (oldEntity.roles ?? []).some(
        role => role.name === Role.ADMIN || role.name === Role.SUPER_ADMIN || role.name === 'super_admin',
      );
      isAdminOther = isAdmin && !isSelf && !targetIsAdmin;
    }

    if (!context.serverContext && !isSuper) {
      for (const field of [
        'samlID',
        'samlDepartmentCode',
        'samlMatrikelnummer',
        'samlGender',
        'stripeCustomerID',
        'zoomAccessTokenID',
        'sd_customerNr',
        'suspentions',
        'lastActiveAt',
        'createdAt',
      ] as const) {
        if (update.has(field)) (update as any)[field] = (oldEntity as any)[field];
      }
    }

    if (update.has('email') && typeof update.email === 'string') {
      // BaseQL captures the MongoDB modifier before beforeUpdate runs.
      const email = update.email === oldEntity.email ? update.email : update.email.trim().toLowerCase();
      const emailChanged = email.toLowerCase() !== oldEntity.email?.toLowerCase();
      if (!context.serverContext && !isSuper && !isAdminOther && !isSelf && emailChanged) {
        throw new ExpectedError('Sie dürfen die E-Mail-Adresse dieses Kontos nicht ändern.');
      }
      if (!context.serverContext && !isSuper && !isAdminOther && emailChanged && oldEntity.emailConfirmed === true) {
        throw new ExpectedError(
          'Ihre E-Mail-Adresse ist bereits bestätigt und kann hier nicht geändert werden. Bitte ändern Sie sie in Ihrem Profil.',
        );
      }
      update.email = email;
      const username = (update as any).doc?.username ?? oldEntity.username;
      if (
        (emailChanged && oldEntity.username?.toLowerCase() === oldEntity.email?.toLowerCase()) ||
        (typeof username === 'string' && username.trim().toLowerCase() === email)
      ) {
        update.username = email;
      }
    }
    if (!context.serverContext && !isSuper && !isAdminOther) {
      const emailChanged = update.has('email') && update.email?.toLowerCase() !== oldEntity.email?.toLowerCase();
      update.emailConfirmed = emailChanged ? false : oldEntity.emailConfirmed;
    }
  }

  static override async beforeUpdate(oldEntity: User, update: User): Promise<void> {
    if (update.email && update.email !== oldEntity.email) {
      const collection = mongo().collection<User>(this._collectionName);
      const count = await collection.countDocuments({
        _id: { $ne: oldEntity._id },
        email: { $regex: `^${update.email.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' },
      });
      if (count > 0) throw new ExpectedError('Ein Nutzer mit dieser E-Mail existiert bereits.');
    }

    const rawFirstName = (update as any).doc?.firstName;
    const rawLastName = (update as any).doc?.lastName;
    const nameChanged =
      (rawFirstName && rawFirstName !== oldEntity.firstName) ||
      (rawLastName && rawLastName !== oldEntity.lastName);
    if (nameChanged && !(context as any).serverContext) {
      const { getCurrentUserServer } = await import('@adornis/bh-util/util.js');
      const caller = await getCurrentUserServer();
      const isAdmin = caller?.roles?.some((r: any) => r.name === Role.ADMIN || r.name === Role.SUPER_ADMIN);
      if (!isAdmin) {
        const participationCollection = mongo().collection<Participation>(Participation._collectionName);
        const hasParticipation = await participationCollection.countDocuments({ userID: oldEntity._id }, { limit: 1 });
        if (hasParticipation > 0) {
          throw new ExpectedError('Namensänderung nicht möglich. Bitte wenden Sie sich an den Veranstalter.');
        }
      }
    }

    return super.beforeUpdate(oldEntity, update);
  }

  static override async beforeCreate(entity: User): Promise<void> {
    if (!context.serverContext) {
      const { getCurrentUserServer } = await import('@adornis/bh-util/util.js');
      const caller = await getCurrentUserServer();
      const isSuper =
        caller?.isSuperAdmin === true || caller?.roles?.some((role: { name: string }) => role.name === Role.SUPER_ADMIN);
      if (!isSuper) {
        entity.roles = [];
        entity.suspentions = [];
        entity.emailConfirmed = false;
        entity.samlID = undefined;
        entity.samlDepartmentCode = undefined;
        entity.samlMatrikelnummer = undefined;
        entity.samlGender = undefined;
        entity.stripeCustomerID = undefined;
        entity.zoomAccessTokenID = null;
        entity.sd_customerNr = null;
        entity.lastActiveAt = null;
      }
    }
    const rawEmail = (entity as any).doc?.email;
    if (typeof rawEmail === 'string') {
      entity.email = rawEmail.trim().toLowerCase();
      const username = (entity as any).doc?.username;
      if (typeof username === 'string' && username.trim().toLowerCase() === entity.email) {
        entity.username = entity.email;
      }
    }
    const collection = mongo().collection<User>(this._collectionName);
    const count = await collection.countDocuments({
      email: { $regex: `^${entity.email.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' },
    });
    if (count > 0) throw new ExpectedError('Ein Nutzer mit dieser E-Mail existiert bereits.');

    return super.beforeCreate(entity);
  }

  override toString() {
    return `${this.firstName} ${this.lastName} (${this.email})`;
  }
}

type RoleLike = { name: string; contexts?: Maybe<string[]> };

/**
 * Lecturer roles are scoped per institution (GLOBAL_CONTEXT = all institutions, same as `hasPermission`).
 * Use this instead of `role.name === Role.LECTURER` whenever the check concerns a specific institution.
 */
export function isLecturerInInstitution(roles: Maybe<RoleLike[]>, institutionID: string) {
  return !!roles?.some(
    role =>
      role.name === Role.LECTURER &&
      (role.contexts?.includes(institutionID) || role.contexts?.includes(GLOBAL_CONTEXT)),
  );
}

/** institution IDs of all lecturer roles; `null` if the user is a global lecturer (= all institutions) */
export function lecturerContextsOf(roles: Maybe<RoleLike[]>): string[] | null {
  const contexts = (roles ?? []).filter(role => role.name === Role.LECTURER).flatMap(role => role.contexts ?? []);
  return contexts.includes(GLOBAL_CONTEXT) ? null : [...new Set(contexts)];
}

function rolesDiffer(a: User['roles'] | undefined, b: User['roles'] | undefined): boolean {
  const arrA = a ?? [];
  const arrB = b ?? [];
  if (arrA.length !== arrB.length) return true;
  const serialize = (role: any) => `${role?.name}:${[...(role?.contexts ?? [])].sort().join(',')}`;
  const aSet = arrA.map(serialize).sort().join('|');
  const bSet = arrB.map(serialize).sort().join('|');
  return aSet !== bSet;
}

function suspensionsDiffer(a: Suspension[] | undefined, b: Suspension[] | undefined): boolean {
  const arrA = a ?? [];
  const arrB = b ?? [];
  if (arrA.length !== arrB.length) return true;
  const serialize = (s: any) =>
    `${s?.institutionID}:${!!s?.blockedNoLimit}:${s?.blockedTill instanceof Date ? s.blockedTill.getTime() : String(s?.blockedTill ?? '')}`;
  const aSet = arrA.map(serialize).sort().join('|');
  const bSet = arrB.map(serialize).sort().join('|');
  return aSet !== bSet;
}
